/**
 * Sharma/RyseNet report API — paths align with Bruno files in RyseNet docs.
 *
 * Default base is the **same-origin proxy** (`/api/sharma-reports`) implemented by
 * Azure Functions (`api/sharmaReportsProxy`) so the browser avoids CORS to the upstream host.
 *
 * Override with REACT_APP_SHARMA_REPORTS_BASE only when needed (e.g. full upstream URL for
 * non-browser tooling — direct browser calls to Azure will hit CORS unless the API sends ACAO).
 */

/** Prefix proxied by `api/sharmaReportsProxy` — must match Function route + SHARMA_REPORT_PATHS. */
export const DEFAULT_SHARMA_REPORTS_PROXY_PREFIX = "/api/sharma-reports";

export function getSharmaReportsBaseUrl() {
  const raw = process.env.REACT_APP_SHARMA_REPORTS_BASE;
  if (raw === undefined || raw === "") {
    return DEFAULT_SHARMA_REPORTS_PROXY_PREFIX;
  }
  return String(raw).replace(/\/$/, "");
}

/**
 * Report period (YYYYMM) — valor fijo de milestone hasta que el backend exponga periodo por portafolio.
 * Canonical query param: `period` (see `sharmaReportsApi`). Legacy alias: `perd`.
 * Override via REACT_APP_SHARMA_REPORT_PERD.
 * Opcional: `portfolio.contextData.reportPeriod` o `.perd` (string YYYYMM válido) si el API de contexto lo envía.
 */
export const DEFAULT_PERD =
  process.env.REACT_APP_SHARMA_REPORT_PERD || "202512";

/** Same value as {@link DEFAULT_PERD}; use in code paths that speak in terms of `period`. */
export const DEFAULT_REPORT_PERIOD = DEFAULT_PERD;

export const MAIN_PORTFOLIO_ALLOCATION_PERIOD = DEFAULT_PERD;

/** Relative paths only — concat with base URL. */
export const SHARMA_REPORT_PATHS = {
  ytdPerformanceByStrategy: "/api/ytd/performance-by-strategy",
  ytdPerformanceByIndex: "/api/ytd/index-performance",
  drawdownsOverTime: "/api/drawdowns/drawdowns-over-time",
  riskAttribution:
    "/api/risk-metrics/portfolio-risk-and-performance-attribution",
  valueAtRisk: "/api/risk-metrics/value-at-risk",
  mcValueAtRisk: "/api/risk-metrics/mc-value-at-risk/nonlinear",
  businessCycleScenario: "/api/risk-metrics/business-cycle-scenario",
  marginalRiskBeta: "/api/risk-metrics/marginal-risk-beta",
  /** Bruno / RyseNet: GET `api/risk-metrics/risk-profile` — spider de seis ejes (claves "1"…"6"). */
  riskProfile: "/api/risk-metrics/risk-profile",
  portfolioAllocation: "/api/portfolio/allocation",
};

/**
 * Sharma API `portfolio_id` codes.
 * Subportfolios under Main: DSPRIVCRE · DSPRIVEQ · DSINTEQ · DSFIXINC · DSREALEST · DSDOMEQ
 * Parent: DSPFOLIO (Main portfolio)
 */
export const SHARMA_PORTFOLIO_API_IDS = {
  DSPRIVCRE: "DSPRIVCRE",
  DSPRIVEQ: "DSPRIVEQ",
  DSINTEQ: "DSINTEQ",
  DSFIXINC: "DSFIXINC",
  DSREALEST: "DSREALEST",
  DSDOMEQ: "DSDOMEQ",
  DSPFOLIO: "DSPFOLIO",
};

/**
 * Maps app numeric working portfolio id (1–6) → Sharma `portfolio_id` (voice keys: ds-*).
 * Sent as canonical `portfolioId` + legacy `portfolio_id` — see `fetchSharmaReportJson`.
 */
export const NUMERIC_PORTFOLIO_TO_API_ID = {
  1: SHARMA_PORTFOLIO_API_IDS.DSPRIVCRE,
  2: SHARMA_PORTFOLIO_API_IDS.DSPRIVEQ,
  3: SHARMA_PORTFOLIO_API_IDS.DSINTEQ,
  4: SHARMA_PORTFOLIO_API_IDS.DSFIXINC,
  5: SHARMA_PORTFOLIO_API_IDS.DSREALEST,
  6: SHARMA_PORTFOLIO_API_IDS.DSDOMEQ,
};

export function portfolioNumericToApiId(portfolioId) {
  const fallback = NUMERIC_PORTFOLIO_TO_API_ID[1];
  if (!Number.isInteger(portfolioId)) {
    return fallback;
  }
  return NUMERIC_PORTFOLIO_TO_API_ID[portfolioId] ?? fallback;
}

/**
 * Voice/UI catalog keys → internal report key for fetch + adapters.
 * The visible table title may come from the adapter/API response; choosing which report
 * to load is always by tool args + this catalog (and TABLE_CATALOG_TO_REPORT_KEY), not
 * by discovering report types from the API at runtime.
 */
export const YTD_PERFORMANCE_BY_INDEX_DISPLAY_TITLE =
  "YTD Performance by Strategy by Index";

export const TABLE_CATALOG_TO_REPORT_KEY = {
  "YTD Performance by index": "ytdByIndex",
  "YTD Performance by Strategy by Index": "ytdByIndex",
  "YTD Performance portfolio": "ytdByStrategy",
  "YTD Performance by Strategy": "ytdByStrategy",
  "Value at Risk": "valueAtRisk",
  "Value at Risk Analysis": "valueAtRisk",
  "Monte Carlo Value at Risk": "mcValueAtRisk",
  "Monte Carlo VaR": "mcValueAtRisk",
  "Valor en Riesgo Monte Carlo": "mcValueAtRisk",
  "VaR Monte Carlo": "mcValueAtRisk",
};

export const BUSINESS_CYCLE_WINDOWS = Object.freeze([
  "Recession",
  "Innovation",
  "Stagflation",
  "Over-Extension",
]);

const BUSINESS_CYCLE_WINDOW_ALIASES = Object.freeze({
  recession: "Recession",
  recesion: "Recession",
  innovation: "Innovation",
  stagflation: "Stagflation",
  estanflacion: "Stagflation",
  "over-extension": "Over-Extension",
  "over extension": "Over-Extension",
  overextension: "Over-Extension",
  sobreexpansion: "Over-Extension",
});

export function resolveBusinessCycleWindow(input) {
  if (input == null || input === "") {
    return null;
  }
  const trimmed = String(input).trim();
  const exact = BUSINESS_CYCLE_WINDOWS.find(
    (w) => w.toLowerCase() === trimmed.toLowerCase()
  );
  if (exact) {
    return exact;
  }
  const stripAccents = (s) =>
    s.normalize("NFD").replace(/\p{M}/gu, "");
  const normalized = stripAccents(trimmed.toLowerCase()).replace(/\s+/g, " ");
  if (BUSINESS_CYCLE_WINDOW_ALIASES[normalized]) {
    return BUSINESS_CYCLE_WINDOW_ALIASES[normalized];
  }
  const noDash = normalized.replace(/-/g, " ");
  return BUSINESS_CYCLE_WINDOW_ALIASES[noDash] ?? null;
}

/** Catalog keys for Sharma-backed tables (e.g. similarity loop in showTable). */
export const SHARMA_TABLE_CATALOG_KEYS = Object.freeze(
  Object.keys(TABLE_CATALOG_TO_REPORT_KEY)
);

/** Voice/UI catalog → chart report keys (paths via SHARMA_REPORT_PATHS). */
export const REPORT_CHART_CATALOG_TO_KEY = {
  "Drawdowns Over Time": "drawdownsOverTime",
  "Risk and Performance Attribution": "riskAttribution",
  /** Bruno: equity-beta-marginal-risk-beta.bru */
  "Equity Beta (Marginal Risk Beta)": "marginalRiskBeta",
  "Marginal Risk Beta": "marginalRiskBeta",
  /** Radar/spider de perfil de riesgo (mismo `portfolio_id` / `perd` que el resto de Sharma). */
  "Risk Profile": "riskProfile",
  /** Alias voz/UI (misma clave interna `riskProfile`). */
  "Perfil de riesgo": "riskProfile",
};
